import React, { useMemo, useState } from "react";
import {
  BarChart3, Share2, Users, FileText, UserCog, DollarSign, ChevronRight,
  CalendarDays, AlertTriangle, Info, Plus, Wrench, Star, Clock, TrendingUp, Download,
} from "lucide-react";

const money = (v) => ("R$ " + Number(v || 0).toFixed(2)).replace(".", ",");
const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
const ACTIVE = ["aberto", "atribuido", "aceito", "a_caminho", "agendado", "em_andamento"];
// SLA (horas) por tipo de serviço — chamado ativo acima disso conta como atrasado
const SLA_HOURS = { "Emergência": 4, "Emergencial": 4, "Conserto": 48, "Instalação": 72, "Manutenção": 72, "Orçamento": 24, "_default": 48 };
const slaFor = (t) => SLA_HOURS[t] ?? SLA_HOURS._default;

function Donut({ done, doing, cancelled }) {
  const total = done + doing + cancelled || 1;
  const segs = [
    { v: done, c: "#1e7be0" }, { v: doing, c: "#10b981" }, { v: cancelled, c: "#ef4444" },
  ];
  const R = 42, C = 2 * Math.PI * R;
  let off = 0;
  return (
    <svg viewBox="0 0 100 100" className="h-32 w-32 -rotate-90">
      <circle cx="50" cy="50" r={R} fill="none" stroke="#eef2f7" strokeWidth="14" />
      {segs.map((s, i) => {
        const len = (s.v / total) * C;
        const el = <circle key={i} cx="50" cy="50" r={R} fill="none" stroke={s.c} strokeWidth="14"
          strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-off} />;
        off += len;
        return el;
      })}
    </svg>
  );
}

function LineChart({ points }) {
  const w = 560, h = 180, pad = 24;
  const max = Math.max(...points.map((p) => p.v), 8);
  const stepX = (w - pad * 2) / Math.max(points.length - 1, 1);
  const xy = points.map((p, i) => [pad + i * stepX, h - pad - (p.v / max) * (h - pad * 2)]);
  const path = xy.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${path} L${xy[xy.length - 1][0].toFixed(1)},${h - pad} L${pad},${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
      {[0, 0.5, 1].map((g, i) => (
        <line key={i} x1={pad} x2={w - pad} y1={pad + g * (h - pad * 2)} y2={pad + g * (h - pad * 2)} stroke="#eef2f7" />
      ))}
      <path d={area} fill="#1e7be0" opacity="0.08" />
      <path d={path} fill="none" stroke="#1e7be0" strokeWidth="2.5" strokeLinejoin="round" />
      {xy.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="3.2" fill="#1e7be0" />)}
    </svg>
  );
}

const FEATURES = [
  { icon: BarChart3, title: "Dashboard Inteligente", desc: "Acompanhe indicadores em tempo real.", tab: "metricas" },
  { icon: Share2, title: "Controle Total", desc: "Gerencie chamados, equipe, prazos e resultados.", tab: "chamados" },
  { icon: Users, title: "Desempenho da Equipe", desc: "Avalie produtividade e qualidade dos atendimentos.", tab: "equipe" },
  { icon: FileText, title: "Relatórios Completos", desc: "Filtros avançados e exportação de dados.", tab: "metricas" },
  { icon: UserCog, title: "Gestão de Clientes", desc: "Histórico de atendimentos e informações centralizadas.", tab: "clientes" },
  { icon: DollarSign, title: "Controle Financeiro", desc: "Faturamento, recebimentos e análise de resultados.", tab: "financeiro" },
];

const QUICK = [
  { icon: Plus, label: "Novo chamado", action: "new" },
  { icon: CalendarDays, label: "Agenda", tab: "agenda" },
  { icon: UserCog, label: "Clientes", tab: "clientes" },
  { icon: Users, label: "Equipe", tab: "equipe" },
  { icon: BarChart3, label: "Relatórios", tab: "metricas" },
  { icon: DollarSign, label: "Financeiro", tab: "financeiro" },
  { icon: Wrench, label: "Serviços", tab: "servicos" },
];

export default function ManagerOverview({ requests = [], team = [], userName, onQuick, onNew }) {
  const [period, setPeriod] = useState(30);
  const m = useMemo(() => {
    const cutoff = Date.now() - period * 864e5;
    const all = requests.filter((r) => !r.created_at || new Date(r.created_at).getTime() >= cutoff);
    const total = all.length;
    const done = all.filter((r) => r.status === "concluido").length;
    const cancelled = all.filter((r) => r.status === "cancelado").length;
    const doing = all.filter((r) => ACTIVE.includes(r.status)).length;

    // atrasados por regra de SLA (chamado ativo há mais que o SLA do tipo)
    const nowMs = Date.now();
    const overdue = all.filter((r) => ACTIVE.includes(r.status) && r.created_at
      && (nowMs - new Date(r.created_at).getTime()) / 36e5 > slaFor(r.service_type));

    // por tipo
    const byType = {};
    all.forEach((r) => { const k = r.service_type || "Outros"; byType[k] = (byType[k] || 0) + 1; });
    const types = Object.entries(byType).map(([k, v]) => ({ k, v })).sort((a, b) => b.v - a.v).slice(0, 5);

    // série por dia
    const byDay = {};
    all.forEach((r) => { const d = (r.created_at || "").slice(0, 10); if (d) byDay[d] = (byDay[d] || 0) + 1; });
    const days = Object.keys(byDay).sort().slice(-14);
    const series = days.length ? days.map((d) => ({ d, v: byDay[d] })) : [{ d: "", v: 0 }];

    // equipe
    const byTech = {};
    all.filter((r) => r.status === "concluido" && r.assigned_technician_name)
      .forEach((r) => { byTech[r.assigned_technician_name] = (byTech[r.assigned_technician_name] || 0) + 1; });
    team.filter((t) => t.role === "technician").forEach((t) => { if (!(t.name in byTech)) byTech[t.name] = byTech[t.name] || 0; });
    const techPerf = Object.entries(byTech).map(([name, v]) => ({ name, v })).sort((a, b) => b.v - a.v).slice(0, 5);

    // satisfação (nota real do cliente)
    const rated = all.map((r) => r.rating ?? r.customer_rating).filter((x) => typeof x === "number");
    const satisfaction = rated.length ? (rated.reduce((a, b) => a + b, 0) / rated.length) : null;

    // tempo médio (h)
    const durations = all.filter((r) => r.status === "concluido" && r.created_at && r.updated_at)
      .map((r) => (new Date(r.updated_at) - new Date(r.created_at)) / 36e5).filter((x) => x >= 0);
    const avgH = durations.length ? durations.reduce((a, b) => a + b, 0) / durations.length : null;

    return { all, total, done, cancelled, doing, overdue, types, series, techPerf, satisfaction, avgH, ratedCount: rated.length };
  }, [requests, team, period]);

  const exportCsv = () => {
    const head = ["Ticket", "Cliente", "Tipo", "Status", "Criado em", "Técnico", "Nota"];
    const rows = m.all.map((r) => [r.ticket_number, r.customer_name, r.service_type, r.status,
      (r.created_at || "").slice(0, 10), r.assigned_technician_name || "", r.rating ?? ""]);
    const summary = [
      [], ["Resumo (últimos " + period + " dias)"],
      ["Total", m.total], ["Concluídos", m.done], ["Em andamento", m.doing],
      ["Cancelados", m.cancelled], ["Atrasados (SLA)", m.overdue.length],
      ["Satisfação média", m.satisfaction ? m.satisfaction.toFixed(1) : "-"],
    ];
    const csv = [head, ...rows, ...summary]
      .map((line) => line.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `visao-geral-${period}dias.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const fmtDur = (h) => h == null ? "—" : `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m`;

  const KPI = ({ label, value, sub, color }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-center" data-testid={`kpi-${label}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-3xl font-extrabold ${color || "text-slate-800"}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
    </div>
  );

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5" data-testid="manager-overview">
      <div className="xl:col-span-2 space-y-5">
        {/* Saudação + KPIs */}
        <div className="rounded-2xl bg-gradient-to-br from-[#0a2a6c] to-[#1e7be0] text-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-2xl font-extrabold">Olá, {userName?.split(" ")[0] || "Gestor"}!</h2>
              <p className="text-white/80 text-sm">Acompanhe os indicadores e resultados da sua operação.</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg bg-white/15 p-0.5" data-testid="period-filter">
                {[7, 30, 90].map((d) => (
                  <button key={d} onClick={() => setPeriod(d)} data-testid={`period-${d}`}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${period === d ? "bg-white text-[#0a2a6c]" : "text-white/90 hover:bg-white/10"}`}>
                    {d} dias
                  </button>
                ))}
              </div>
              <button onClick={exportCsv} data-testid="export-report"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 text-white text-sm font-medium hover:bg-white/25 transition-colors">
                <Download className="h-4 w-4" /> Exportar
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI label="Total de Chamados" value={m.total} sub="no período" />
          <KPI label="Concluídos" value={m.done} sub={`${pct(m.done, m.total)}% de conclusão`} color="text-[#0a2a6c]" />
          <KPI label="Em Andamento" value={m.doing} sub={`${pct(m.doing, m.total)}% da operação`} color="text-[#1e7be0]" />
          <KPI label="Cancelados" value={m.cancelled} sub={`${pct(m.cancelled, m.total)}% cancelamento`} color="text-red-500" />
        </div>

        {/* Desempenho geral */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="overview-line-chart">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-slate-800 flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-[#1e7be0]" /> Desempenho geral</h3>
            <button onClick={() => onQuick?.("metricas")} className="text-sm text-[#1e7be0] hover:underline">Ver relatório completo</button>
          </div>
          <LineChart points={m.series} />
        </div>

        {/* Status + Tipo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="font-semibold text-slate-800 mb-3 text-sm">CHAMADOS POR STATUS</h3>
            <div className="flex items-center gap-4">
              <Donut done={m.done} doing={m.doing} cancelled={m.cancelled} />
              <div className="space-y-1.5 text-sm">
                <p className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-[#1e7be0]" /> Concluídos <b>{m.done}</b></p>
                <p className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-emerald-500" /> Em andamento <b>{m.doing}</b></p>
                <p className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-red-500" /> Cancelados <b>{m.cancelled}</b></p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="font-semibold text-slate-800 mb-3 text-sm">CHAMADOS POR TIPO</h3>
            <div className="space-y-2.5">
              {m.types.length === 0 && <p className="text-sm text-slate-400">Sem dados.</p>}
              {m.types.map((t) => (
                <div key={t.k} className="flex items-center gap-3 text-sm">
                  <span className="w-24 text-slate-600 truncate">{t.k}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-[#1e7be0] rounded-full" style={{ width: `${pct(t.v, m.total)}%` }} />
                  </div>
                  <span className="text-slate-500 w-16 text-right">{t.v} ({pct(t.v, m.total)}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Equipe */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 text-sm">DESEMPENHO DA EQUIPE TÉCNICA</h3>
            <button onClick={() => onQuick?.("equipe")} className="text-sm text-[#1e7be0] hover:underline">Ver todos</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {m.techPerf.length === 0 && <p className="text-sm text-slate-400">Sem técnicos.</p>}
            {m.techPerf.map((t) => (
              <div key={t.name} className="text-center rounded-xl bg-slate-50 p-3">
                <div className="h-10 w-10 rounded-full bg-[#0a2a6c] text-white flex items-center justify-center mx-auto mb-1 font-semibold">{t.name[0]}</div>
                <p className="text-xs font-medium text-slate-700 truncate">{t.name}</p>
                <p className="text-lg font-bold text-[#1e7be0] leading-none">{t.v}</p>
                <p className="text-[10px] text-slate-400">Concluídos</p>
              </div>
            ))}
          </div>
        </div>

        {/* Satisfação + tempo médio */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4">
            <Star className="h-10 w-10 text-amber-400 fill-amber-400" />
            <div>
              <p className="text-xs text-slate-500 uppercase">Satisfação do cliente</p>
              <p className="text-2xl font-bold text-slate-800">{m.satisfaction ? m.satisfaction.toFixed(1) : "—"}</p>
              <p className="text-[11px] text-slate-400">Com base em {m.ratedCount} avaliações</p>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4">
            <Clock className="h-10 w-10 text-[#1e7be0]" />
            <div>
              <p className="text-xs text-slate-500 uppercase">Tempo médio de atendimento</p>
              <p className="text-2xl font-bold text-slate-800">{fmtDur(m.avgH)}</p>
              <p className="text-[11px] text-slate-400">Entre abertura e conclusão</p>
            </div>
          </div>
        </div>

        {/* Acesso rápido */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="font-semibold text-slate-800 text-sm mb-3">ACESSO RÁPIDO</h3>
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-3">
            {QUICK.map(({ icon: Icon, label, tab, action }) => (
              <button key={label} onClick={() => action === "new" ? onNew?.() : onQuick?.(tab)}
                className="flex flex-col items-center gap-1.5 group" data-testid={`quick-${label}`}>
                <span className="h-11 w-11 rounded-full bg-blue-50 flex items-center justify-center text-[#1e7be0] group-hover:bg-[#1e7be0] group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="text-[11px] text-slate-600 text-center leading-tight">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Coluna direita: features + alertas */}
      <div className="space-y-3">
        {FEATURES.map(({ icon: Icon, title, desc, tab }) => (
          <button key={title} onClick={() => onQuick?.(tab)}
            className="w-full text-left rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3 hover:shadow-md hover:-translate-y-0.5 transition-all"
            data-testid={`feature-${tab}`}>
            <span className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center text-[#1e7be0] flex-shrink-0"><Icon className="h-6 w-6" /></span>
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-slate-800 text-sm">{title}</span>
              <span className="block text-xs text-slate-500 leading-tight">{desc}</span>
            </span>
            <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
          </button>
        ))}

        <div className="rounded-2xl bg-[#0a2a6c] text-white p-4 space-y-3">
          <h3 className="font-semibold text-sm">ALERTAS E NOTIFICAÇÕES</h3>
          <div className="rounded-xl bg-white/95 text-slate-800 p-3 flex items-center gap-3">
            <CalendarDays className="h-6 w-6 text-[#1e7be0] flex-shrink-0" />
            <div className="min-w-0 flex-1"><p className="text-sm font-semibold">{requests.filter((r) => r.scheduled_date === new Date().toISOString().slice(0, 10)).length} agendamentos para hoje</p><p className="text-xs text-slate-500">Confira na agenda do dia.</p></div>
          </div>
          <div className="rounded-xl bg-white/95 text-slate-800 p-3 flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0" />
            <div className="min-w-0 flex-1"><p className="text-sm font-semibold">{m.overdue.length} chamados atrasados</p><p className="text-xs text-slate-500">Fora do prazo de SLA — precisam de atenção.</p></div>
          </div>
          <div className="rounded-xl bg-white/95 text-slate-800 p-3 flex items-center gap-3">
            <Info className="h-6 w-6 text-[#1e7be0] flex-shrink-0" />
            <div className="min-w-0 flex-1"><p className="text-sm font-semibold">Relatórios disponíveis</p><p className="text-xs text-slate-500">Acesse e confira os resultados.</p></div>
          </div>
        </div>
      </div>
    </div>
  );
}
